import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { fetchStocks } from '../../api/stock';

export function NotificationDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { data: stocks = [] } = useQuery({
    queryKey: ['stocks', false],
    queryFn: () => fetchStocks(false),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const lowStock = stocks.filter((s) => s.quantity <= s.threshold);
  const count = lowStock.length;

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      {/* Bell button */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 text-saas-muted hover:text-saas-text hover:bg-saas-bg rounded-lg transition-all duration-150"
        title="Stock Alerts"
        aria-label="Notifications"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center w-4 h-4 rounded-full bg-saas-error text-white text-[10px] font-bold leading-none">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-card-hover border border-saas-border z-50 overflow-hidden animate-fade-in">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-saas-border">
            <span className="text-sm font-semibold text-saas-text">Stock Alerts</span>
            {count > 0 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                {count} low
              </span>
            )}
          </div>

          {/* List */}
          <div className="max-h-72 overflow-y-auto divide-y divide-saas-border">
            {count === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-saas-muted">
                <svg className="w-8 h-8 mb-2 text-saas-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-sm font-medium text-saas-muted">All stocks are healthy</p>
              </div>
            ) : (
              lowStock.map((item) => (
                <div key={item.id} className="flex items-center gap-3 px-4 py-3 hover:bg-red-50 transition-colors">
                  {/* Warning icon */}
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                    <svg className="w-4 h-4 text-saas-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                      />
                    </svg>
                  </div>
                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-saas-text truncate">{item.name}</p>
                    <p className="text-xs text-saas-muted">
                      {item.quantity} {item.unit} remaining &bull; threshold: {item.threshold}
                    </p>
                  </div>
                  {/* Qty badge */}
                  <span className="flex-shrink-0 text-xs font-bold text-saas-error bg-red-100 px-2 py-0.5 rounded">
                    {item.quantity}/{item.threshold}
                  </span>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-saas-border px-4 py-2">
            <button
              onClick={() => { setOpen(false); void navigate('/stock'); }}
              className="w-full text-center text-xs font-medium text-brand-600 hover:text-brand-700 py-1"
            >
              View all stock →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
